<template>
	<div class="user-edit-page">
		<PageHeader
			:data="{
				name: user.name,
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Users', to: '/users' },
				],
			}"
		/>

		<div v-if="!loading" class="row">
			<!-- User Card -->
			<div class="col-xl-4 col-lg-5 col-md-5 order-1 order-md-0">
				<div class="card">
					<div class="card-body">
						<div class="user-avatar-section">
							<div class="d-flex align-items-center flex-column">
								<Avatar :user="user" :size="128" class="mt-3 mb-2" />
								<div class="user-info text-center">
									<h4>{{ user.name }}</h4>
									<span class="badge badge-light-primary">Author</span>
								</div>
							</div>
						</div>

						<h4 class="fw-bolder border-bottom pb-2 mb-1">Details</h4>
						<div class="info-container">
							<ul class="list-unstyled">
								<li class="mb-2">
									<span class="fw-bolder me-2">Email:</span>
									<span>{{ user.email }}</span>
								</li>
								<li class="mb-2">
									<span class="fw-bolder me-2">Status:</span>
									<span class="badge bg-success">Active</span>
								</li>
								<li class="mb-2">
									<span class="fw-bolder me-2">Role:</span>
									<span>Author</span>
								</li>
								<li class="mb-2">
									<span class="fw-bolder me-2">Country:</span>
									<span>{{ user.country }}</span>
								</li>
							</ul>
							<button
								v-if="authUser.permissions.includes('USER_UPDATE') && user.force_logout === 0"
								class="btn btn-info float-end w-100 mb-2"
								@click="logoutUser(user)"
							>
								Logout
							</button>
							<button
								v-if="authUser.permissions.includes('USER_DELETE')"
								class="btn btn-danger float-end w-100"
								@click="confirmDelete(user)"
							>
								Remove
							</button>
						</div>
					</div>
				</div>
			</div>
			<!-- #User Card -->
			<div class="col-xl-8 col-lg-7 col-md-7 order-0 order-md-1">
				<!-- User navigation -->
				<ul class="nav nav-tabs mb-3">
					<li class="nav-item">
						<router-link
							class="nav-link"
							:class="this.$route.name === 'userOverview' ? 'active' : ''"
							aria-current="page"
							:to="'/users/' + this.$route.params.id"
						>
							<font-awesome-icon :icon="['fas', 'user']" /> Overview
						</router-link>
					</li>
					<li class="nav-item">
						<router-link
							class="nav-link"
							:class="this.$route.name === 'userInformation' ? 'active' : ''"
							aria-current="page"
							:to="'/users/' + this.$route.params.id + '/information'"
						>
							<font-awesome-icon :icon="['fas', 'info-circle']" /> Information
						</router-link>
					</li>
					<li class="nav-item" style="display:none">
						<router-link
							class="nav-link"
							:class="this.$route.name === 'userTimeline' ? 'active' : ''"
							aria-current="page"
							:to="'/users/' + this.$route.params.id + '/timeline'"
						>
							<font-awesome-icon :icon="['fas', 'clock']" /> Timeline
						</router-link>
					</li>
					<li class="nav-item" v-if="authUser.permissions.includes('ROLE_ACCESS')">
						<router-link
							class="nav-link"
							:class="this.$route.name === 'userPermissions' ? 'active' : ''"
							aria-current="page"
							:to="'/users/' + this.$route.params.id + '/permissions'"
						>
							<font-awesome-icon :icon="['fas', 'lock']" /> Permissions
						</router-link>
					</li>
				</ul>
				<!-- #User navigation -->
				<router-view :person="user"></router-view>
			</div>
		</div>
		<div v-else class="text-center">
			<div class="spinner-border" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../../api'
import { mapState } from 'vuex'
import PageHeader from '../../../components/layout/PageHeader.vue'
import Avatar from '@/components/user/Avatar'
import { handleErrors } from '../../../actions/helpers'
export default {
	name: 'EditParent',
	components: { PageHeader, Avatar },
	data() {
		return {
			user: {},
			loading: false,
			permissions: {},
		}
	},
	computed: {
		...mapState(['authUser']),
	},
	created() {
		this.loadUser()
	},
	methods: {
		loadUser() {
			this.loading = true
			restApi
				.get('user/' + this.$route.params.id)
				.then(({ data }) => {
					this.user = data
					this.loading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},
		confirmDelete(user) {
			this.$swal
				.fire({
					title: 'Are you sure?',
					text: "You won't be able to revert this!",
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, delete it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						// Remove from backend
						if (this.user.id === this.authUser.id) {
							this.$swal.fire({
								icon: 'error',
								title: 'You cannot perform this action',
							})
						} else {
							restApi
								.delete('/user/' + user.id)
								.then(() => {
									this.$swal.fire({
										icon: 'success',
										title: 'User ' + user.name + ' successfully removed!',
									})
									this.$router.push('/users')
								})
								.catch(error => {
									this.$swal.fire({
										icon: 'error',
										title: handleErrors(error, ''),
										timer: 6000,
									})
								})
						}
					}
				})
		},

		logoutUser(user) {
			this.$swal
				.fire({
					title: 'Are you sure want to force logout this user?',
					text: "You won't be able to revert this!",
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, logout!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						// Remove from backend
						if (this.user.id === this.authUser.id) {
							this.$swal.fire({
								icon: 'error',
								title: 'You cannot force logout yourself!',
							})
						} else {
							restApi
								.post('/user/logout/' + user.id)
								.then(({ data }) => {
									this.user = data
									this.$swal.fire({
										icon: 'success',
										title: 'User ' + user.name + ' successfully logged out!',
									})
								})
								.catch(error => {
									this.$swal.fire({
										icon: 'error',
										title: handleErrors(error, ''),
										timer: 6000,
									})
								})
						}
					}
				})
		},
	},
}
</script>

<style scoped>
/* Settings-language pass — deep selectors reach the router-view tab children */
.user-edit-page >>> .card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	box-shadow: none;
}

.user-edit-page >>> .card-header {
	background: transparent;
	border-bottom: 1px solid #f1f3f5;
	padding-bottom: 10px;
}

/* Tabs — settings style: blue bottom-line, no solid pill */
.user-edit-page >>> .nav-tabs {
	border-bottom: 1px solid #e6e8ec;
}
.user-edit-page >>> .nav-tabs .nav-link {
	border: none;
	background: transparent;
	color: #6b7280;
	font-weight: 600;
	font-size: 13px;
	padding: 8px 14px;
	transition: color 0.15s ease, box-shadow 0.15s ease;
}
.user-edit-page >>> .nav-tabs .nav-link:hover {
	color: #111827;
}
.user-edit-page >>> .nav-tabs .nav-link.active,
.user-edit-page >>> .nav-tabs .nav-link.router-link-exact-active.router-link-active {
	background-color: transparent !important;
	color: var(--bs-primary);
	box-shadow: inset 0 -2px 0 var(--bs-primary);
}

/* Inputs */
.user-edit-page >>> .form-control,
.user-edit-page >>> .form-select {
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	box-shadow: none;
}
.user-edit-page >>> .form-control:focus,
.user-edit-page >>> .form-select:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}
.user-edit-page >>> .form-check-input:checked {
	background-color: #111827;
	border-color: #111827;
}

/* Buttons → flat settings language */
.user-edit-page >>> .btn-primary {
	background: transparent;
	color: #111827;
	border: 1px solid #111827;
	border-radius: 8px;
	font-weight: 600;
	box-shadow: none;
}
.user-edit-page >>> .btn-primary:hover {
	background: #f3f5f9;
	color: #111827;
}
.user-edit-page >>> .btn-info,
.user-edit-page >>> .btn-secondary {
	background: transparent;
	color: #6b7280;
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	font-weight: 600;
	box-shadow: none;
}
.user-edit-page >>> .btn-info:hover,
.user-edit-page >>> .btn-secondary:hover {
	background: #f3f5f9;
	color: #111827;
	border-color: #d1d5db;
}
.user-edit-page >>> .btn-danger {
	background: transparent;
	color: #dc3545;
	border: 1px solid #f1c2c7;
	border-radius: 8px;
	font-weight: 600;
	box-shadow: none;
}
.user-edit-page >>> .btn-danger:hover {
	background: #fdecee;
	color: #dc3545;
}

/* Badges → soft pills */
.user-edit-page >>> .badge.bg-success {
	background: #d1e7dd !important;
	color: #0f5132 !important;
	font-weight: 600;
}
.user-edit-page >>> .badge.badge-light-primary {
	background: #e7f1ff;
	color: #0b5ed7;
	font-weight: 600;
}
</style>
